import jsCookie from 'js-cookie';
import qs from 'qs';
import { useLocation } from 'react-router';
import type { PageType } from 'types';

import { deriveReferrerParts } from '~/utils/sourceMedium';

import useEnv from './useEnv';
import useRegion from './useRegion';

const plpRegex = /^(\/mens|\/womens)?\/release-dates\/(.*\/)?(.*\/)?$/;
const pdpRegex = /^\/(release-dates|clothing)\/.*\/.*\/.*\/$/;

export type MarketingCookies = {
  tss_lid: string;
  tss_ttclid: string;
  tss_user_landing_page: PageType;
  tssm: {
    campaign?: string;
    channel?: string;
    derived?: boolean;
    medium?: string;
  };
};

export default function useMarketingCookies() {
  const { pathname, search } = useLocation();
  const { COOKIES_DOMAIN } = useEnv();
  const region = useRegion();

  const options = {
    domain: COOKIES_DOMAIN ?? region.domain,
    expires: 30,
    path: '/'
  };

  function load() {
    loadUtm();
    loadLid();
    loadTtclid();
    loadFbc();
    loadLandingPage();
  }

  function getLandingPage() {
    return jsCookie.get('tss_user_landing_page') as
      | MarketingCookies['tss_user_landing_page']
      | undefined;
  }

  function setCookie(name: keyof MarketingCookies, value: string) {
    return jsCookie.set(name, value, options);
  }

  function loadUtm() {
    const qsParams = parseQueryString(search);
    const tssm = getTssm();

    if (hasSomeTssmValues(qsParams)) {
      // A campaign landing always beats a referrer-derived first touch.
      if (tssm && !tssm.derived) return;

      const tssmCookieValue = JSON.stringify({
        campaign: qsParams.utm_campaign,
        channel: qsParams.utm_source,
        medium: qsParams.utm_medium
      });

      return setCookie('tssm', tssmCookieValue);
    }

    if (tssm) return;

    const { source, medium } = deriveReferrerParts(
      document.referrer,
      window.location.hostname
    );

    setCookie(
      'tssm',
      JSON.stringify({ channel: source, derived: true, medium })
    );
  }

  function getTssm(): MarketingCookies['tssm'] | undefined {
    const raw = jsCookie.get('tssm');

    if (!raw) return;

    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }

  function loadLid() {
    const qsParams = parseQueryString(search);
    const lid = qsParams.lid as string;

    if (lid) return setCookie('tss_lid', lid);
  }

  function loadTtclid() {
    const qsParams = parseQueryString(search);
    const ttclid = qsParams.ttclid as string;

    if (ttclid) setCookie('tss_ttclid', ttclid);
  }

  function loadFbc() {
    const qsParams = parseQueryString(search);
    const fbclid = qsParams.fbclid as string;

    if (!fbclid) return;

    const existingFbc = jsCookie.get('_fbc');
    const existingFbclid = existingFbc?.split('.').slice(3).join('.');

    if (existingFbclid === fbclid) return;

    jsCookie.set('_fbc', `fb.1.${Date.now()}.${fbclid}`, options);
  }

  function loadLandingPage() {
    const landingPageCookie = getLandingPage();

    if (!landingPageCookie) {
      const landingPage = getLandingPageValue(pathname);
      setCookie('tss_user_landing_page', landingPage);
    }
  }

  return {
    getLandingPage,
    load
  };
}

function parseQueryString(search: string) {
  return qs.parse(search, { ignoreQueryPrefix: true });
}

function hasSomeTssmValues(target: qs.ParsedQs) {
  return (
    Boolean(target?.utm_campaign) ||
    Boolean(target?.utm_medium) ||
    Boolean(target?.utm_source)
  );
}

function getLandingPageValue(pathname: string): PageType {
  if (pdpRegex.test(pathname)) return 'pdp';
  if (plpRegex.test(pathname)) return 'plp';
  if (pathname === '/contact/') return 'contact';

  return 'unknown';
}
